import React, { useState, useEffect } from 'react';
import { 
  Bot, Search, AlertTriangle, CheckCircle, XCircle, RefreshCw, Send,
  Code, FileText, Database, Layers, ArrowRight, Lightbulb, Copy,
  ChevronDown, ChevronRight, TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import { toast } from 'sonner';
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// ═══════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL - AUDIT IA
// ═══════════════════════════════════════════════════════════════
const ProjectAuditIA = () => {
  const [activeTab, setActiveTab] = useState('rapport');
  const [auditReport, setAuditReport] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);

  useEffect(() => {
    loadAuditReport();
    // Message de bienvenue du chat
    setChatMessages([{
      role: 'assistant',
      content: "Bonjour ! Je suis l'agent d'audit du projet SAN-IA CRM. Je peux vous expliquer les incohérences détectées, proposer des solutions, ou répondre à vos questions sur l'architecture du projet. Que souhaitez-vous savoir ?"
    }]);
  }, []);

  const loadAuditReport = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/api/project-audit/report`);
      if (response.ok) {
        const data = await response.json();
        setAuditReport(data);
      } else {
        // Utiliser le rapport par défaut
        setAuditReport(getDefaultAuditReport());
      }
    } catch (error) {
      console.error('Erreur chargement audit:', error);
      setAuditReport(getDefaultAuditReport());
    }
    setLoading(false);
  };

  const getDefaultAuditReport = () => ({
    score: 7.5,
    date: new Date().toISOString(),
    summary: {
      total_issues: 5,
      critical: 1,
      warnings: 2,
      suggestions: 2,
      positive_points: 12
    },
    issues: [
      {
        id: 1,
        severity: 'critical',
        title: "server.py trop volumineux",
        description: "Le fichier server.py fait plus de 25000 lignes. Cela rend la maintenance difficile et augmente le risque de bugs.",
        recommendation: "Découper en modules : routes/, services/, models/",
        affected_files: ['/app/backend/server.py'],
        effort: 'high'
      },
      {
        id: 2,
        severity: 'info',
        title: "Collection clients_pipeline supprimée",
        description: "La collection legacy 'clients_pipeline' a été supprimée. Seule 'pipeline_clients' est utilisée.",
        recommendation: "Aucune action nécessaire",
        affected_files: [],
        effort: 'low'
      },
      {
        id: 3,
        severity: 'warning',
        title: "Duplication de logique comptable",
        description: "ConsolidationComptable.js et ConsolidationComptableNew.js contiennent du code similaire",
        recommendation: "Fusionner les deux composants ou supprimer l'ancien",
        affected_files: ['/app/frontend/src/components/ConsolidationComptable.js', '/app/frontend/src/components/ConsolidationComptableNew.js'],
        effort: 'medium'
      },
      {
        id: 4,
        severity: 'suggestion',
        title: "Hot-reload Pennylane cassé",
        description: "Le singleton pennylane_service empêche le hot-reload du backend",
        recommendation: "Implémenter un pattern de lazy loading pour le service",
        affected_files: ['/app/backend/pennylane_service_v2.py'],
        effort: 'medium'
      },
      {
        id: 5,
        severity: 'suggestion',
        title: "Cartes Pennylane en double dans UI",
        description: "La section Super Batard affiche deux cartes de configuration Pennylane",
        recommendation: "Supprimer la carte dupliquée",
        affected_files: ['/app/frontend/src/SuperBatardDashboard.js'],
        effort: 'low'
      }
    ],
    positive_points: [
      "Architecture frontend React bien structurée",
      "Utilisation cohérente de TailwindCSS",
      "API FastAPI avec documentation",
      "Système de permissions granulaire",
      "Intégration Google Sheets fonctionnelle",
      "Modal client configurable (WYSIWYG)",
      "Système de templates multi-métiers",
      "Audit logs pour traçabilité",
      "Transcription automatique des appels",
      "Centre de connaissances implémenté",
      "Gestion des onglets dynamiques",
      "Preview avec données client réel"
    ]
  });

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setLoadingChat(true);

    try {
      const response = await authFetch(`${API}/api/project-audit/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: chatInput,
          context: auditReport
        })
      });

      if (response.ok) {
        const data = await response.json();
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        // Réponse simulée si l'API n'existe pas encore
        const simulatedResponse = generateSimulatedResponse(chatInput, auditReport);
        setChatMessages(prev => [...prev, { role: 'assistant', content: simulatedResponse }]);
      }
    } catch (error) {
      const simulatedResponse = generateSimulatedResponse(chatInput, auditReport);
      setChatMessages(prev => [...prev, { role: 'assistant', content: simulatedResponse }]);
    }
    
    setLoadingChat(false);
  };

  const generateSimulatedResponse = (question, report) => {
    const q = question.toLowerCase();
    
    if (q.includes('server.py') || q.includes('serveur')) {
      return "Le fichier server.py est effectivement un problème majeur. Avec plus de 25000 lignes, il contient toute la logique API. Je recommande de le découper en modules :\n\n- `/routes/` : endpoints par domaine (clients, factures, etc.)\n- `/services/` : logique métier\n- `/models/` : schémas Pydantic\n\nCela prendra du temps mais améliorera considérablement la maintenabilité.";
    }
    
    if (q.includes('clients_pipeline') || q.includes('collection')) {
      return "La collection `clients_pipeline` a été supprimée. La source unique de données clients est `pipeline_clients`. Les champs personnalisés sont dans `client_custom_data`.";
    }
    
    if (q.includes('pennylane') || q.includes('hot-reload')) {
      return "Le service Pennylane utilise un pattern singleton qui est initialisé une fois au démarrage. Le problème est que lors d'un hot-reload, le module Python n'est pas rechargé complètement, ce qui cause des incohérences. Solutions possibles :\n\n1. Utiliser un lazy loading\n2. Implémenter un pattern de factory\n3. Redémarrer manuellement le backend après modifications Pennylane";
    }
    
    if (q.includes('score') || q.includes('note')) {
      return `Le score actuel est de ${report?.score || 7.5}/10. Les points forts sont l'architecture frontend, le système de templates, et les intégrations. Les points à améliorer sont principalement la structure du backend (server.py monolithique) et quelques duplications de code.`;
    }
    
    return "Je peux vous aider avec des questions sur :\n- La structure du projet\n- Les incohérences détectées\n- Les recommandations d'amélioration\n- L'architecture technique\n\nQue souhaitez-vous savoir précisément ?";
  };

  const tabs = [
    { id: 'rapport', label: 'Rapport d\'Audit', icon: FileText },
    { id: 'chat', label: 'Discussion avec l\'Agent', icon: Bot },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <Bot className="w-8 h-8 text-purple-400" />
          <h2 className="text-2xl font-bold text-white">Audit IA</h2>
        </div>
        <p className="text-slate-400">
          Analyse automatique du code, détection des incohérences et recommandations
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === tab.id
                ? 'bg-purple-500 text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-purple-400" />
          </div>
        ) : (
          <>
            {activeTab === 'rapport' && auditReport && (
              <AuditReportTab report={auditReport} onRefresh={loadAuditReport} />
            )}

            {activeTab === 'chat' && (
              <ChatTab 
                messages={chatMessages}
                input={chatInput}
                setInput={setChatInput}
                onSend={sendChatMessage}
                loading={loadingChat}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TAB: RAPPORT D'AUDIT
// ═══════════════════════════════════════════════════════════════
const AuditReportTab = ({ report, onRefresh }) => {
  const [expandedIssues, setExpandedIssues] = useState({});

  const toggleIssue = (id) => {
    setExpandedIssues(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const severityConfig = {
    critical: { color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30', icon: XCircle, label: 'Critique' },
    warning: { color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30', icon: AlertTriangle, label: 'Attention' },
    suggestion: { color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/30', icon: Lightbulb, label: 'Suggestion' }
  };

  const effortLabels = {
    low: { label: 'Facile', color: 'text-emerald-400' },
    medium: { label: 'Moyen', color: 'text-amber-400' },
    high: { label: 'Complexe', color: 'text-red-400' }
  };

  return (
    <div className="space-y-6">
      {/* Score global */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`text-5xl font-bold ${
            report.score >= 8 ? 'text-emerald-400' :
            report.score >= 6 ? 'text-amber-400' :
            'text-red-400'
          }`}>
            {report.score}/10
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Score Global</h3>
            <p className="text-sm text-slate-400">
              Dernière analyse : {new Date(report.date).toLocaleDateString('fr-FR')}
            </p>
          </div>
        </div>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm flex items-center gap-2"
        >
          <RefreshCw size={16} />
          Actualiser
        </button>
      </div>

      {/* Résumé */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-slate-900/50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-white">{report.summary.total_issues}</p>
          <p className="text-xs text-slate-400">Total problèmes</p>
        </div>
        <div className="bg-red-500/10 rounded-lg p-4 text-center border border-red-500/20">
          <p className="text-2xl font-bold text-red-400">{report.summary.critical}</p>
          <p className="text-xs text-slate-400">Critiques</p>
        </div>
        <div className="bg-amber-500/10 rounded-lg p-4 text-center border border-amber-500/20">
          <p className="text-2xl font-bold text-amber-400">{report.summary.warnings}</p>
          <p className="text-xs text-slate-400">Avertissements</p>
        </div>
        <div className="bg-blue-500/10 rounded-lg p-4 text-center border border-blue-500/20">
          <p className="text-2xl font-bold text-blue-400">{report.summary.suggestions}</p>
          <p className="text-xs text-slate-400">Suggestions</p>
        </div>
        <div className="bg-emerald-500/10 rounded-lg p-4 text-center border border-emerald-500/20">
          <p className="text-2xl font-bold text-emerald-400">{report.summary.positive_points}</p>
          <p className="text-xs text-slate-400">Points positifs</p>
        </div>
      </div>

      {/* Problèmes détectés */}
      <div>
        <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
          Incohérences Détectées
        </h4>
        <div className="space-y-3">
          {report.issues.map(issue => {
            const config = severityConfig[issue.severity];
            const Icon = config.icon;
            
            return (
              <div key={issue.id} className={`border rounded-lg overflow-hidden ${config.border}`}>
                <button
                  onClick={() => toggleIssue(issue.id)}
                  className={`w-full flex items-center justify-between p-4 ${config.bg} hover:opacity-90 transition-opacity`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${config.color}`} />
                    <div className="text-left">
                      <span className={`text-xs px-2 py-0.5 rounded ${config.bg} ${config.color}`}>
                        {config.label}
                      </span>
                      <h5 className="font-medium text-white mt-1">{issue.title}</h5>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs ${effortLabels[issue.effort].color}`}>
                      {effortLabels[issue.effort].label}
                    </span>
                    {expandedIssues[issue.id] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </div>
                </button>
                
                {expandedIssues[issue.id] && (
                  <div className="p-4 bg-slate-900/50 border-t border-slate-700 space-y-3">
                    <div>
                      <span className="text-xs text-slate-500 uppercase">Description</span>
                      <p className="text-slate-300 mt-1">{issue.description}</p>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                      <span className="text-xs text-emerald-400 uppercase">💡 Recommandation</span>
                      <p className="text-slate-300 mt-1">{issue.recommendation}</p>
                    </div>
                    {issue.affected_files.length > 0 && (
                      <div>
                        <span className="text-xs text-slate-500 uppercase">Fichiers concernés</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {issue.affected_files.map((file, i) => (
                            <code key={i} className="text-xs bg-slate-800 text-slate-400 px-2 py-1 rounded">
                              {file}
                            </code>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Points positifs */}
      <div>
        <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-emerald-400" />
          Points Positifs
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {report.positive_points.map((point, idx) => (
            <div key={idx} className="flex items-center gap-2 p-2 bg-emerald-500/10 rounded-lg">
              <CheckCircle size={14} className="text-emerald-400 flex-shrink-0" />
              <span className="text-sm text-slate-300">{point}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// TAB: CHAT AVEC L'AGENT
// ═══════════════════════════════════════════════════════════════
const ChatTab = ({ messages, input, setInput, onSend, loading }) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
        <Bot className="w-5 h-5 text-purple-400" />
        Discussion avec l'Agent d'Audit
      </h3>

      {/* Zone de messages */}
      <div className="h-96 overflow-y-auto bg-slate-900/50 rounded-lg p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-lg p-3 ${
              msg.role === 'user' 
                ? 'bg-purple-500 text-white' 
                : 'bg-slate-800 text-slate-300'
            }`}>
              {msg.role === 'assistant' && (
                <div className="flex items-center gap-2 mb-2 text-purple-400 text-xs">
                  <Bot size={14} />
                  Agent d'Audit
                </div>
              )}
              <p className="whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-800 rounded-lg p-3">
              <RefreshCw className="w-5 h-5 animate-spin text-purple-400" />
            </div>
          </div>
        )}
      </div>

      {/* Zone de saisie */}
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && !loading && onSend()}
          placeholder="Posez une question sur le projet..."
          className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-purple-500 focus:outline-none"
          disabled={loading}
        />
        <button
          onClick={onSend}
          disabled={loading || !input.trim()}
          className="px-4 py-3 bg-purple-500 hover:bg-purple-600 disabled:bg-purple-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};

export default ProjectAuditIA;
